import type {
  ApiResponse,
  CheckStatusResponse,
  LoginResponse,
  QRCodeResponse,
  UserInfo,
} from '@/types/auth'
import { QRCodeStatus } from '@/types/qrcode'
import { generateDingTalkLoginQRCode } from '@/utils/qrcode'

// Mock用户信息
export const mockUser: UserInfo = {
  unionId: 'user_001',
  // dingtalkUserId: 'dingtalk_user_001',
  nick: '张三',
  name: '张三',
  avatarUrl: 'https://static.dingtalk.com/media/lADPDgQ9q8hAzQHNAzQC_640_820.jpg',
  // mobile: '13800138000',
  // email: 'zhangsan@company.com',
  // jobNumber: 'EMP001',
  // position: '仓库管理员',
  // departments: [
  //   {
  //     id: 'dept_001',
  //     dingtalkDeptId: 'dingtalk_dept_001',
  //     name: '仓储部',
  //     parentId: undefined,
  //     orderNum: 1,
  //     isActive: true,
  //     createdAt: '2024-01-01T00:00:00Z'
  //   }
  // ],
  // roles: [
  //   {
  //     id: 'role_001',
  //     name: '仓库管理员',
  //     code: 'warehouse_manager',
  //     description: '仓库管理员角色',
  //     permissions: [
  //       'inventory.view',
  //       'inventory.create',
  //       'inventory.update',
  //       'inbound.view',
  //       'inbound.create',
  //       'outbound.view',
  //       'outbound.create'
  //     ],
  //     createdAt: '2024-01-01T00:00:00Z'
  //   }
  // ],
  // isActive: true,
  createdAt: '2024-01-01T00:00:00Z',
  updatedAt: '2024-01-01T00:00:00Z'
}

// Mock权限列表
export const mockPermissions = [
  'inventory.view',
  'inventory.create',
  'inventory.update',
  'inbound.view',
  'inbound.create',
  'outbound.view',
  'outbound.create'
]

// 模拟网络延迟
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// 生成随机的登录密钥
const generateLoginKey = () => 'login_key_' + Math.random().toString(36).substring(2)


// Mock状态管理
class MockStateManager {
  private qrCodeStates = new Map<string, {
    status: QRCodeStatus;
    createdAt: number;
    expireTime: number;
  }>()

  createQRCode(loginKey: string) {
    const now = Date.now()
    const expireTime = now + 5 * 60 * 1000 // 5分钟后过期
    
    this.qrCodeStates.set(loginKey, {
      status: QRCodeStatus.PENDING,
      createdAt: now,
      expireTime
    })

    // 模拟扫码流程：2秒后变为已扫码，再3秒后确认登录
    setTimeout(() => {
      const state = this.qrCodeStates.get(loginKey)
      if (state && state.status === QRCodeStatus.PENDING) {
        state.status = QRCodeStatus.SCANNED
        
        // 再过3秒确认登录
        setTimeout(() => {
          const currentState = this.qrCodeStates.get(loginKey)
          if (currentState && currentState.status === QRCodeStatus.SCANNED) {
            currentState.status = QRCodeStatus.CONFIRMED
          }
        }, 3000)
      }
    }, 2000)

    return expireTime
  }

  getQRCodeStatus(loginKey: string) {
    const state = this.qrCodeStates.get(loginKey)
    if (!state) {
      return { status: QRCodeStatus.EXPIRED }
    }

    // 检查是否过期
    if (Date.now() > state.expireTime && state.status !== QRCodeStatus.CONFIRMED) {
      state.status = QRCodeStatus.EXPIRED
    }

    return state
  }

  cleanup() {
    // 清理过期的二维码状态
    const now = Date.now()
    for (const [key, state] of this.qrCodeStates) {
      if (now > state.expireTime) {
        this.qrCodeStates.delete(key)
      }
    }
  }
}

const mockStateManager = new MockStateManager()

// 定期清理过期状态
setInterval(() => {
  mockStateManager.cleanup()
}, 60000) // 每分钟清理一次

export const mockAuthService = {
  // 企业内免登录
  async enterpriseLogin(authCode: string, corpId: string): Promise<LoginResponse> {
    await delay(800)
    console.log(`企业内免登录: authCode=${authCode}, corpId=${corpId}`)
    return {
      success: true,
      data: {
        token: 'mock_jwt_token_' + Date.now(),
        refreshToken: 'mock_refresh_token_' + Date.now(),
        user: mockUser,
        permissions: mockPermissions
      },
      message: '登录成功'
    }
  },

  // 获取扫码登录二维码
  async getQRCode(): Promise<QRCodeResponse> {
    await delay(500)
    
    const loginKey = generateLoginKey()
    const expireTime = mockStateManager.createQRCode(loginKey)
    const qrCode = await generateDingTalkLoginQRCode(loginKey)
    
    return {
      success: true,
      data: {
        qrCode,
        loginKey,
        expireTime
      },
      message: '二维码生成成功'
    }
  },

  // 检查扫码登录状态
  async checkQRCodeStatus(loginKey: string): Promise<CheckStatusResponse> {
    await delay(300)
    
    const state = mockStateManager.getQRCodeStatus(loginKey)
    
    if (state.status === QRCodeStatus.CONFIRMED) {
      return {
        success: true,
        data: {
          status: QRCodeStatus.CONFIRMED,
          token: 'mock_jwt_token_' + Date.now(),
          refreshToken: 'mock_refresh_token_' + Date.now(),
          user: mockUser,
          permissions: mockPermissions
        },
        message: '登录成功'
      }
    }
    
    return {
      success: true,
      data: {
        status: state.status
      },
      message: '状态检查成功'
    }
  },

  // 刷新Token
  async refreshToken(refreshToken: string): Promise<ApiResponse<{
    token: string;
    refreshToken: string;
  }>> {
    await delay(400)
    console.log(`刷新Token: refreshToken=${refreshToken}`)
    
    return {
      success: true,
      data: {
        token: 'mock_jwt_token_' + Date.now(),
        refreshToken: 'mock_refresh_token_' + Date.now()
      },
      message: 'Token刷新成功',
      timestamp: Date.now()
    }
  },

  // 退出登录
  async logout(): Promise<ApiResponse> {
    await delay(200)
    
    return {
      success: true,
      message: '退出登录成功',
      timestamp: Date.now()
    }
  },

  // 获取个人信息
  async getUserProfile(): Promise<ApiResponse<UserInfo>> {
    await delay(300)
    
    return {
      success: true,
      data: mockUser,
      message: '获取用户信息成功',
      timestamp: Date.now()
    }
  },

  // 获取用户权限
  async getUserPermissions(): Promise<ApiResponse<{
    permissions: string[];
    rolePermissions: Array<{
      roleId: string;
      roleName: string;
      permissions: string[];
    }>;
  }>> {
    await delay(400)
    
    return {
      success: true,
      data: {
        permissions: mockPermissions,
        rolePermissions: [
          {
            roleId: 'role_001',
            roleName: '仓库管理员',
            permissions: mockPermissions
          }
        ]
      },
      message: '获取权限信息成功',
      timestamp: Date.now()
    }
  },

  // 检查权限
  async checkPermission(resource: string, action: string): Promise<ApiResponse<{
    hasPermission: boolean;
    reason?: string;
  }>> {
    await delay(200)
    
    const permission = `${resource}.${action}`
    const hasPermission = mockPermissions.includes(permission)
    
    return {
      success: true,
      data: {
        hasPermission,
        reason: hasPermission ? undefined : '用户无此权限'
      },
      message: '权限检查成功',
      timestamp: Date.now()
    }
  }
}